/** App UI font-size preference. Persisted in localStorage and applied as a
 *  root font-size on <html>; because the UI chrome is sized with Tailwind's
 *  rem-based classes, this scales the whole app UI (toolbar, menus, dialogs).
 *  The Pixi canvas renders independently and is unaffected. Mirrors the direct
 *  localStorage pattern used by cursor-preference.ts — no global settings
 *  context exists. Default: medium. */

const STORAGE_KEY = 'stitchpress-font-size';

const FONT_SIZES = ['small', 'medium', 'large', 'x-large', 'xx-large'] as const;
export type FontSize = (typeof FONT_SIZES)[number];

/** Root <html> font-size applied per preset. `medium` matches the browser
 *  default (16px), so it leaves the root font-size untouched. */
const ROOT_FONT_SIZE: Record<FontSize, string> = {
    small: '14px',
    medium: '16px',
    large: '18px',
    'x-large': '20px',
    'xx-large': '22px',
};

type Listener = (size: FontSize) => void;
const listeners = new Set<Listener>();

function isFontSize(value: string | null): value is FontSize {
    return (FONT_SIZES as readonly string[]).includes(value ?? '');
}

export function getFontSize(): FontSize {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return isFontSize(stored) ? stored : 'medium';
    } catch {
        return 'medium';
    }
}

/** Apply without persisting — a live preview. Notifies subscribers so any
 *  mounted picker reflects the previewed value. */
export function applyFontSize(size: FontSize): void {
    applyRootFontSize(size);
    for (const l of listeners) l(size);
}

/**
 * Persist without applying. Call this only on an explicit commit (Apply, or a
 * standalone instant switcher such as the editor gear menu). Auto-persisting
 * every change would make an unapplied preview survive a page reload — the
 * same reasoning as `persistLanguage` in `@/i18n`.
 */
export function persistFontSize(size: FontSize): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, size);
    } catch {
        // Ignore storage failures (private mode, etc.).
    }
}

/** Persist and apply in one step: the instant-switcher path. */
export function setFontSize(size: FontSize): void {
    persistFontSize(size);
    applyFontSize(size);
}

export function onFontSizeChange(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Apply the current persisted preference to <html>. Call once at startup. */
export function initFontSize(): void {
    applyRootFontSize(getFontSize());
}

function applyRootFontSize(size: FontSize): void {
    if (typeof document === 'undefined') return;
    document.documentElement.style.fontSize = ROOT_FONT_SIZE[size];
}
